// src/components/Support/TicketList.jsx - WITH SCROLLABLE TABLE
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Box,
  Avatar,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  Visibility,
  Reply,
  Delete,
  Schedule,
  Storefront,
  Close,
} from '@mui/icons-material';
import StatusBadge from './StatusBadge';
import { SUPPORT_CATEGORIES, getCategoryLabel, getPriorityConfig } from '../../constants/supportConstants';

// Helper to get vendor avatar initials
const getVendorInitials = (vendorName) => {
  if (!vendorName) return 'S';
  const words = vendorName.split(' ');
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

// Helper to get vendor color
const getVendorColor = (vendorName) => {
  const colors = [
    '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
    '#e65100', '#00838f', '#2e7d32', '#6a1b9a',
    '#c62828', '#00695c', '#4a148c', '#e65100'
  ];
  let hash = 0;
  if (vendorName) {
    for (let i = 0; i < vendorName.length; i++) {
      hash = vendorName.charCodeAt(i) + ((hash << 5) - hash);
    }
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const TicketList = ({
  tickets,
  onViewTicket,
  onStatusUpdate,
  onReplyTicket,
  onDeleteTicket,
  onCloseTicket,
  loading = false,
  isVendor = false,
  showVendor = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getPriorityChip = (priority) => {
    const config = getPriorityConfig(priority);
    const colorMap = {
      info: 'info',
      warning: 'warning',
      error: 'error',
    };
    return (
      <Chip
        label={config.label}
        color={colorMap[config.color] || 'default'}
        size="small"
      />
    );
  };

  // Handle click with safe function checking
  const handleAction = (callback, ticket) => {
    if (typeof callback === 'function') {
      callback(ticket);
    } else {
      console.warn('Callback is not a function', callback);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography color="text.secondary">Loading tickets...</Typography>
      </Box>
    );
  }

  if (!tickets || tickets.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
        <Typography color="text.secondary">No tickets found</Typography>
      </Paper>
    );
  }

  // Mobile Card View
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tickets.map((ticket) => {
          const vendorName = ticket.Vendor?.storeName || 'N/A';
          const vendorInitials = getVendorInitials(vendorName);
          const vendorColor = getVendorColor(vendorName);
          const vendorLogo = ticket.Vendor?.storeLogo || null;

          return (
            <Card key={ticket.id} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1} minWidth={0}>
                      <Typography variant="subtitle2" fontWeight="bold" noWrap>
                        {ticket.subject || 'No subject'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ticket.ticketNumber || `#${ticket.id}`}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5} flexShrink={0}>
                      <StatusBadge status={ticket.status} size="small" />
                      {getPriorityChip(ticket.priority)}
                    </Box>
                  </Box>

                  {showVendor && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        src={vendorLogo}
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: vendorColor,
                          fontSize: '10px',
                        }}
                      >
                        {!vendorLogo && vendorInitials}
                      </Avatar>
                      <Typography variant="body2" noWrap color="text.secondary">
                        {vendorName}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {ticket.category ? getCategoryLabel(ticket.category) : 'General'} • {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      {typeof onViewTicket === 'function' && (
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(onViewTicket, ticket)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {typeof onStatusUpdate === 'function' && (
                        <Tooltip title="Update Status">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(onStatusUpdate, ticket)}
                          >
                            <Schedule fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {typeof onReplyTicket === 'function' && (
                        <Tooltip title="Reply">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(onReplyTicket, ticket)}
                          >
                            <Reply fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isVendor && typeof onCloseTicket === 'function' && ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                        <Tooltip title="Close Ticket">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(onCloseTicket, ticket.id)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isVendor && typeof onDeleteTicket === 'function' && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleAction(onDeleteTicket, ticket.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }

  // ✅ Tablet & Desktop: Scrollable Table View
  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer sx={{ 
        overflowX: 'auto',  // ✅ Makes table scrollable horizontally
        maxWidth: '100%',
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f1f1',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : '#c1c1c1',
          borderRadius: 4,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#a8a8a8',
          },
        },
      }}>
        <Table sx={{ 
          minWidth: isTablet ? 900 : 750,  // ✅ Ensures table is wide enough to scroll on tablet
          '& .MuiTableCell-root': {
            padding: isTablet ? '12px 8px' : '16px',
          },
        }}>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell sx={{ minWidth: 100 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Ticket #
                </Typography>
              </TableCell>
              {showVendor && (
                <TableCell sx={{ minWidth: 140 }}>
                  <Typography variant="subtitle2" fontWeight="bold" noWrap>
                    Vendor
                  </Typography>
                </TableCell>
              )}
              <TableCell sx={{ minWidth: 150 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Subject
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Category
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Status
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Priority
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Created
                </Typography>
              </TableCell>
              <TableCell align="right" sx={{ minWidth: 160 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((ticket) => {
              const vendorName = ticket.Vendor?.storeName || 'N/A';
              const vendorInitials = getVendorInitials(vendorName);
              const vendorColor = getVendorColor(vendorName);
              const vendorLogo = ticket.Vendor?.storeLogo || null;

              return (
                <TableRow key={ticket.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="500" noWrap>
                      {ticket.ticketNumber || `#${ticket.id}`}
                    </Typography>
                  </TableCell>
                  {showVendor && (
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1} noWrap>
                        <Avatar
                          src={vendorLogo}
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: vendorColor,
                            fontSize: '11px',
                          }}
                        >
                          {!vendorLogo && vendorInitials}
                        </Avatar>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                          {vendorName}
                        </Typography>
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                      {ticket.subject || 'No subject'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryLabel(ticket.category)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={ticket.status} size="small" />
                  </TableCell>
                  <TableCell>{getPriorityChip(ticket.priority)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                      {typeof onViewTicket === 'function' && (
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleAction(onViewTicket, ticket)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {typeof onStatusUpdate === 'function' && (
                        <Tooltip title="Update Status">
                          <IconButton size="small" onClick={() => handleAction(onStatusUpdate, ticket)}>
                            <Schedule fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {typeof onReplyTicket === 'function' && (
                        <Tooltip title="Reply">
                          <IconButton size="small" onClick={() => handleAction(onReplyTicket, ticket)}>
                            <Reply fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isVendor && typeof onCloseTicket === 'function' && ticket.status !== 'closed' && ticket.status !== 'resolved' && (
                        <Tooltip title="Close Ticket">
                          <IconButton size="small" onClick={() => handleAction(onCloseTicket, ticket.id)}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isVendor && typeof onDeleteTicket === 'function' && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleAction(onDeleteTicket, ticket.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TicketList;